import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  disablePush,
  enablePush,
  getPushPermission,
  getSubscription,
  pushSupported,
} from "@/lib/push";

/** Push-notification opt-in for the current device. Requests the browser
 *  allow/deny permission on Enable; lets the user turn it back off. */
export const PushToggle = () => {
  const [supported] = useState(pushSupported());
  const [permission, setPermission] = useState(getPushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSubscription()
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, []);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      setPermission(Notification.permission);
      if (res?.subscribed) {
        setSubscribed(true);
        toast.success("Notifications enabled on this device");
      } else if (res?.permission === "denied") {
        toast.error("Notifications are blocked in your browser settings");
      }
    } catch {
      toast.error("Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast.success("Notifications turned off");
    } catch {
      toast.error("Could not turn off notifications");
    } finally {
      setBusy(false);
    }
  };

  const blocked = permission === "denied";
  return (
    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-black/10 bg-white p-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {subscribed ? (
          <Bell size={18} className="shrink-0 text-brand" />
        ) : (
          <BellOff size={18} className="shrink-0 text-brand-subtle" />
        )}
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-brand-ink">Push notifications</p>
          <p className="text-[11px] text-brand-muted">
            {blocked
              ? "Blocked in your browser settings"
              : subscribed
                ? "On for this device"
                : "Get alerts on this device"}
          </p>
        </div>
      </div>
      {blocked ? null : subscribed ? (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={disable}
          className="h-9 shrink-0 rounded-[10px]"
        >
          Turn off
        </Button>
      ) : (
        <Button
          size="sm"
          disabled={busy}
          onClick={enable}
          className="h-9 shrink-0 rounded-[10px] bg-brand-forest font-semibold text-white"
        >
          Enable
        </Button>
      )}
    </div>
  );
};
